import React, { useEffect, useState, useMemo } from 'react';
import {
  Loader2, ArrowUp, ArrowDown, Minus, AlertTriangle, ThumbsUp, HelpCircle,
  ThumbsDown, ChevronDown, ChevronUp, Info, ShieldAlert,
} from 'lucide-react';
import { TierBadge } from './TierBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = async (url, opts = {}) => {
  const token = localStorage.getItem('backoffice_token');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 90000);
  let res;
  try {
    res = await fetch(url, {
      ...opts,
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
  } finally {
    clearTimeout(timeoutId);
  }
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const ACTION_STYLES = {
  CONTACT_TODAY: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  ENRICH_FIRST: 'bg-amber-100 text-amber-800 border-amber-300',
  FOLLOW_UP: 'bg-blue-100 text-blue-800 border-blue-300',
  WATCH: 'bg-gray-100 text-gray-600 border-gray-300',
  NOT_SHOWN: 'bg-red-50 text-red-500 border-red-200',
};
const ACTION_LABELS = {
  CONTACT_TODAY: 'Contact Today', ENRICH_FIRST: 'Enrich First',
  FOLLOW_UP: 'Follow Up', WATCH: 'Watch', NOT_SHOWN: 'Not Shown',
};

const ActionBadge = ({ action }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${ACTION_STYLES[action] || ACTION_STYLES.WATCH}`}
    data-testid={`canonical-action-badge-${action}`}>
    {ACTION_LABELS[action] || action}
  </span>
);

const RankDelta = ({ delta }) => {
  if (delta === null || delta === undefined) return <span className="text-[10px] text-gray-400">n/a</span>;
  if (delta === 0) return <span className="flex items-center gap-0.5 text-[11px] text-gray-400"><Minus className="h-3 w-3" /> 0</span>;
  if (delta > 0) return <span className="flex items-center gap-0.5 text-[11px] font-bold text-emerald-600" data-testid="rank-delta-up"><ArrowUp className="h-3 w-3" /> {delta}</span>;
  return <span className="flex items-center gap-0.5 text-[11px] font-bold text-red-600" data-testid="rank-delta-down"><ArrowDown className="h-3 w-3" /> {Math.abs(delta)}</span>;
};

const SummaryStat = ({ label, value, accent }) => (
  <div className="text-center px-3">
    <p className={`text-lg font-bold ${accent || 'text-[#0A1F44]'}`}>{value ?? 0}</p>
    <p className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</p>
  </div>
);

const DimBar = ({ label, value, max }) => (
  <div className="flex items-center gap-1.5 text-[10px]">
    <span className="text-gray-500 w-14 shrink-0">{label}</span>
    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full bg-[#0A1F44] rounded-full" style={{ width: (value / max) * 100 + '%' }} />
    </div>
    <span className="text-gray-600 font-medium w-8 text-right">{value}/{max}</span>
  </div>
);

const REASON_CATEGORY_LABELS = {
  great_target: 'Great target', wrong_company_size: 'Wrong company size',
  weak_hiring_pain: 'Weak hiring pain', wrong_niche: 'Wrong niche',
  duplicate_identity: 'Duplicate identity', bad_firmographic_data: 'Bad firmographic data',
  wrong_action_tier: 'Wrong action tier', contact_readiness_issue: 'Contact/readiness issue', other: 'Other',
};

const AccountRow = ({ entry, onReview, reviewMode }) => {
  const [expanded, setExpanded] = useState(false);
  const [reasonCategory, setReasonCategory] = useState(entry.reason_category || '');
  const can = entry.canonical;
  const cur = entry.current;
  if (!can) return null;
  const dims = can.dimensions;
  const safeName = entry.company.replace(/\s+/g, '-');

  return (
    <div className="border-b border-gray-100 hover:bg-gray-50" data-testid={'canonical-row-' + safeName}>
      <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="w-10 text-center">
          <p className="text-sm font-bold text-[#0A1F44]">{can.rank || '—'}</p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#0A1F44] truncate">{entry.company}</p>
            <ActionBadge action={can.recommended_action} />
            {can.best_niche && <span className="text-[10px] text-gray-400">{can.best_niche}</span>}
            {entry.universe !== 'both' && (
              <span className="text-[9px] text-orange-500 border border-orange-200 rounded px-1" data-testid="universe-flag">
                {entry.universe === 'current_only' ? 'Current-only' : 'Canonical-only'}
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 truncate">{(can.score_reasons || []).slice(0, 2).join(' • ')}</p>
        </div>
        <div className="text-center w-24 shrink-0">
          <p className="text-sm font-bold text-[#0A1F44]">{can.canonical_score}<span className="text-[10px] text-gray-400">/100</span></p>
          <p className="text-[9px] text-gray-400">canonical</p>
        </div>
        <div className="text-center w-28 shrink-0">
          {cur ? (
            <p className="text-xs font-medium text-gray-600 flex items-center gap-1 justify-center">#{cur.rank} <TierBadge tier={cur.priority_tier} /></p>
          ) : <p className="text-[10px] text-gray-400">not in current pool</p>}
        </div>
        <div className="w-16 shrink-0 text-right">
          <RankDelta delta={entry.rank_delta} />
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </div>

      {expanded && (
        <div className="px-4 pb-3 pl-14 grid grid-cols-2 gap-4" data-testid={'canonical-row-expanded-' + safeName}>
          <div className="space-y-1">
            <DimBar label="Hiring" value={dims.hiring_signal_strength} max={35} />
            <DimBar label="Commercial" value={dims.commercial_fit} max={35} />
            <DimBar label="Confidence" value={dims.data_confidence} max={15} />
            <DimBar label="Readiness" value={dims.outreach_readiness} max={15} />
            <p className="text-[10px] text-gray-500 mt-1">Readiness status: <strong className="text-gray-700">{can.readiness_status}</strong> · Data confidence: <strong className="text-gray-700">{can.data_confidence}</strong></p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-semibold text-gray-600">Why canonical placed it here:</p>
            <ul className="text-[10px] text-gray-500 list-disc list-inside space-y-0.5">
              {(can.score_reasons || []).map((r, i) => <li key={i}>{r}</li>)}
              {(can.recommended_action_reasons || []).map((r, i) => <li key={'a' + i} className="text-blue-500">{r}</li>)}
            </ul>
            {can.exclusion_reasons && can.exclusion_reasons.length > 0 && (
              <p className="text-[10px] text-red-500 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Excluded: {can.exclusion_reasons.join(', ')}</p>
            )}
          </div>
          {reviewMode && (
            <div className="col-span-2 flex items-center gap-2 pt-2 border-t border-gray-100 flex-wrap" data-testid={'review-controls-' + safeName}>
              <span className="text-[10px] text-gray-400 mr-1">Reviewer verdict:</span>
              {['agree', 'question', 'disagree'].map(v => (
                <button key={v} onClick={(e) => { e.stopPropagation(); onReview(entry.company, v, reasonCategory); }}
                  data-testid={'review-btn-' + v + '-' + safeName}
                  className={'flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border transition-colors ' + (
                    entry.reviewer_verdict === v
                      ? (v === 'agree' ? 'bg-emerald-600 text-white border-emerald-600' : v === 'disagree' ? 'bg-red-600 text-white border-red-600' : 'bg-amber-500 text-white border-amber-500')
                      : 'text-gray-500 border-gray-200 hover:bg-gray-50'
                  )}>
                  {v === 'agree' && <ThumbsUp className="h-3 w-3" />}
                  {v === 'question' && <HelpCircle className="h-3 w-3" />}
                  {v === 'disagree' && <ThumbsDown className="h-3 w-3" />}
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
              <select value={reasonCategory} onClick={e => e.stopPropagation()}
                onChange={e => setReasonCategory(e.target.value)}
                data-testid={'reason-category-' + safeName}
                className="text-[10px] border border-gray-200 rounded px-1.5 py-1">
                <option value="">Reason category (optional)</option>
                {Object.entries(REASON_CATEGORY_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FILTER_ACTIONS = ['CONTACT_TODAY', 'ENRICH_FIRST', 'FOLLOW_UP', 'WATCH', 'NOT_SHOWN'];
const FILTER_READINESS = ['ready', 'partial', 'unknown', 'stale', 'negative'];
const FILTER_CONFIDENCE = ['high', 'medium', 'low'];

const CanonicalPreview = () => {
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [identityAudit, setIdentityAudit] = useState([]);
  const [queueMode, setQueueMode] = useState('quick'); // 'quick' (20) | 'extended' (~90) | 'curated' (40+ stratified)
  const [loading, setLoading] = useState(true);
  const [showQueue, setShowQueue] = useState(true);
  const [showIdentity, setShowIdentity] = useState(false);
  const [filters, setFilters] = useState({ action: '', readiness: '', confidence: '', min_score: '', max_score: '' });
  const [loadError, setLoadError] = useState(null);

  const loadQueue = (mode) => {
    const path = mode === 'extended' ? '/review-queue-extended' : mode === 'curated' ? '/review-queue-curated' : '/review-queue';
    fetchAuth(API_URL + '/api/intelligence/canonical' + path)
      .then(rq => setQueue(rq.queue)).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    setLoadError(null);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const queuePath = queueMode === 'extended' ? '/review-queue-extended' : queueMode === 'curated' ? '/review-queue-curated' : '/review-queue';
    Promise.all([
      fetchAuth(API_URL + '/api/intelligence/canonical/comparison?' + params.toString()),
      fetchAuth(API_URL + '/api/intelligence/canonical' + queuePath),
      fetchAuth(API_URL + '/api/intelligence/canonical/review-analytics'),
      fetchAuth(API_URL + '/api/intelligence/canonical/identity-audit?top_n=500'),
    ]).then(([cmp, rq, an, idn]) => { setData(cmp); setQueue(rq.queue); setAnalytics(an); setIdentityAudit(idn.groups); })
      .catch((e) => { setLoadError(e.message || 'Failed to load canonical data'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filters.action, filters.readiness, filters.confidence]); // eslint-disable-line
  useEffect(() => { if (data) loadQueue(queueMode); }, [queueMode]); // eslint-disable-line

  const handleReview = async (company, verdict, reasonCategory) => {
    try {
      await fetchAuth(API_URL + '/api/intelligence/canonical/review', {
        method: 'POST', body: JSON.stringify({ company: company, verdict: verdict, reason_category: reasonCategory || null }),
      });
      setQueue(q => q.map(item => item.company === company ? { ...item, reviewer_verdict: verdict, reason_category: reasonCategory } : item));
      fetchAuth(API_URL + '/api/intelligence/canonical/review-analytics').then(setAnalytics).catch(() => {});
    } catch (e) { /* silent — internal diagnostic tool */ }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.results.filter(e => e.canonical);
    if (filters.min_score) r = r.filter(e => e.canonical.canonical_score >= Number(filters.min_score));
    if (filters.max_score) r = r.filter(e => e.canonical.canonical_score <= Number(filters.max_score));
    return r;
  }, [data, filters.min_score, filters.max_score]);

  if (loading && !data) return (
    <div className="flex items-center justify-center py-24 text-gray-400 gap-2" data-testid="canonical-preview-loading">
      <Loader2 className="h-5 w-5 animate-spin" /> Computing canonical shadow scores across the full pool...
    </div>
  );

  if (loadError && !data) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="canonical-preview-error">
      <AlertTriangle className="h-6 w-6 text-red-500" />
      <p className="text-sm text-gray-600">Failed to load canonical data (server error {loadError}). This is a load failure, not a real zero-count result.</p>
      <button onClick={load} data-testid="canonical-preview-retry-btn"
        className="px-4 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
        Retry
      </button>
    </div>
  );

  const s = (data && data.summary) || {};

  return (
    <div className="p-6 space-y-4" data-testid="canonical-preview-root">
      <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs rounded-lg px-3 py-2">
        <Info className="h-3.5 w-3.5 shrink-0" />
        Internal validation view only. Production Priority Accounts ranking is unaffected by anything shown here.
      </div>

      {loadError && (
        <div className="flex items-center justify-between gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2" data-testid="canonical-preview-stale-error-banner">
          <span><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />Last refresh failed (server error {loadError}) — showing previously loaded data, not live.</span>
          <button onClick={load} data-testid="canonical-preview-refresh-retry-btn" className="underline font-medium">Retry</button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-between flex-wrap gap-y-2" data-testid="comparison-summary">
        <SummaryStat label="Total Scored" value={s.total_scored} />
        <SummaryStat label="Contact Today" value={s.action_distribution && s.action_distribution.CONTACT_TODAY} accent="text-emerald-700" />
        <SummaryStat label="Enrich First" value={s.action_distribution && s.action_distribution.ENRICH_FIRST} accent="text-amber-700" />
        <SummaryStat label="Follow Up" value={s.action_distribution && s.action_distribution.FOLLOW_UP} accent="text-blue-700" />
        <SummaryStat label="Watch" value={s.action_distribution && s.action_distribution.WATCH} />
        <SummaryStat label="Not Shown" value={s.action_distribution && s.action_distribution.NOT_SHOWN} accent="text-red-500" />
        <SummaryStat label="Top-20 Overlap" value={(s.top20_overlap || 0) + '/20'} />
        <SummaryStat label="Top-50 Overlap" value={(s.top50_overlap || 0) + '/50'} />
        <SummaryStat label="Movers ≥25 Up" value={s.movers_up_25plus} accent="text-emerald-600" />
        <SummaryStat label="Movers ≥25 Down" value={s.movers_down_25plus} accent="text-red-500" />
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="canonical-filters">
        <select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
          data-testid="filter-action-tier" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">All Action Tiers</option>
          {FILTER_ACTIONS.map(a => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
        </select>
        <select value={filters.readiness} onChange={e => setFilters(f => ({ ...f, readiness: e.target.value }))}
          data-testid="filter-readiness" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">All Readiness</option>
          {FILTER_READINESS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filters.confidence} onChange={e => setFilters(f => ({ ...f, confidence: e.target.value }))}
          data-testid="filter-confidence" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">All Confidence</option>
          {FILTER_CONFIDENCE.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input type="number" placeholder="Min score" value={filters.min_score}
          onChange={e => setFilters(f => ({ ...f, min_score: e.target.value }))}
          data-testid="filter-min-score" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-24" />
        <input type="number" placeholder="Max score" value={filters.max_score}
          onChange={e => setFilters(f => ({ ...f, max_score: e.target.value }))}
          data-testid="filter-max-score" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-24" />
        <button onClick={load} data-testid="apply-score-filter" className="text-xs bg-[#0A1F44] text-white rounded-lg px-3 py-1.5">Apply</button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="review-biggest-movers">
        <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 border-b border-amber-100">
          <span className="text-sm font-semibold text-amber-800 flex items-center gap-2 cursor-pointer" onClick={() => setShowQueue(!showQueue)}>
            <AlertTriangle className="h-3.5 w-3.5" /> Review Biggest Movers ({queue.length})
          </span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white border border-amber-200 rounded-md p-0.5" data-testid="queue-mode-toggle">
              <button onClick={() => setQueueMode('quick')} data-testid="queue-mode-quick"
                className={'px-2 py-0.5 text-[10px] font-medium rounded ' + (queueMode === 'quick' ? 'bg-amber-500 text-white' : 'text-amber-700')}>
                Quick (20)
              </button>
              <button onClick={() => setQueueMode('extended')} data-testid="queue-mode-extended"
                className={'px-2 py-0.5 text-[10px] font-medium rounded ' + (queueMode === 'extended' ? 'bg-amber-500 text-white' : 'text-amber-700')}>
                Extended (~90)
              </button>
              <button onClick={() => setQueueMode('curated')} data-testid="queue-mode-curated"
                className={'px-2 py-0.5 text-[10px] font-medium rounded ' + (queueMode === 'curated' ? 'bg-amber-500 text-white' : 'text-amber-700')}>
                Curated (40+)
              </button>
            </div>
            <button onClick={() => setShowQueue(!showQueue)}>
              {showQueue ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
            </button>
          </div>
        </div>
        {showQueue && (
          <div className="max-h-[500px] overflow-y-auto">
            {queue.map((entry, i) => <AccountRow key={i} entry={entry} onReview={handleReview} reviewMode />)}
          </div>
        )}
      </div>

      {/* Review Feedback Analytics */}
      {analytics && (
        <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="review-analytics-panel">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Review Feedback Analytics</p>
          {analytics.total_reviews === 0 ? (
            <p className="text-xs text-gray-400" data-testid="review-analytics-empty">{analytics.note}</p>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-4 text-xs">
                <span className="text-gray-500">{analytics.total_reviews} review(s) submitted</span>
                <span className="text-emerald-700 font-semibold">Agree {analytics.overall.agree}%</span>
                <span className="text-amber-700 font-semibold">Question {analytics.overall.question}%</span>
                <span className="text-red-600 font-semibold">Disagree {analytics.overall.disagree}%</span>
              </div>
              {analytics.note && <p className="text-[10px] text-gray-400" data-testid="review-analytics-note">{analytics.note}</p>}
            </div>
          )}
        </div>
      )}

      {/* Identity / Duplicate Audit */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="identity-audit-card">
        <div className="flex items-center justify-between px-4 py-2.5 bg-purple-50 border-b border-purple-100 cursor-pointer" onClick={() => setShowIdentity(!showIdentity)}>
          <span className="text-sm font-semibold text-purple-800 flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5" /> Identity / Duplicate Audit — Top 500 ({identityAudit.length})</span>
          {showIdentity ? <ChevronUp className="h-4 w-4 text-purple-600" /> : <ChevronDown className="h-4 w-4 text-purple-600" />}
        </div>
        {showIdentity && (
          <div className="divide-y divide-gray-100">
            {identityAudit.length === 0 && <p className="px-4 py-3 text-xs text-gray-400">No name-collision candidates found in the Top 500.</p>}
            {identityAudit.map((g, i) => (
              <div key={i} className="px-4 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={'identity-group-' + i}>
                <span className="text-xs text-gray-700">{g.members.map(m => m.company).join('  ↔  ')}</span>
                <span className={'text-[10px] font-bold px-2 py-0.5 rounded border ' + (
                  g.classification === 'CONFIRMED_ALIAS' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                  g.classification === 'CONFLICTING_DATA' ? 'bg-red-100 text-red-700 border-red-300' :
                  'bg-amber-100 text-amber-700 border-amber-300'
                )}>{g.classification.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="canonical-full-list">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
          All Scored Accounts ({rows.length.toLocaleString()}) — ranked by canonical score
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {rows.slice(0, 300).map((entry, i) => <AccountRow key={i} entry={entry} />)}
        </div>
      </div>
    </div>
  );
};

export default CanonicalPreview;
